import { Component, OnInit, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {SureFlowConfigurationService} from '../../services/sure-flow-configuration.service';
import {StringUtils} from '../../utils/string-utils';
import {Router} from '@angular/router';
import {EntitySureFlowUnit} from '../../model/entity-sure-flow-unit';
import {SpecificationCharacteristic} from '../../entity/specification-characteristic';
import {Entity} from '../../entity/entity';
import {EntitySureFlowLoop} from '../../model/entity-sure-flow-loop';
import {UnitSelectionService} from '../../services/unit-selection-service';
import {RatingsModel} from '../../model/ratings-model';
import {DialogService} from '../../services/dialog.service';
import {firstValueFrom, forkJoin, Observable, Subject} from 'rxjs';
import {ModelService} from '../../services/model-service';
import {EntityLine} from '../../entity/entity-line';
import {Specification} from '../../entity/specification';
import {EntityLineService} from '../../services/entity-line-service';
import {EntityLineHeader} from '../../entity/entity-line-header';
import {GuruEntity} from '../../model/guru-entity';
import {SureFlowEntityRatingInput} from '../../model/sure-flow-entity-rating-input';
import {SureFlowEntityUnitRatingInput} from '../../model/sure-flow-entity-unit-rating-input';
import {TagInformation} from '../../model/tag-information';
import {SelectionSystemType} from '../../model/selection-system-type';
import {SureFlowUnitData} from '../../model/sure-flow-unit-data';
import {SureFlowRatingUnit} from '../../model/sure-flow-rating-unit';
import {SureFlowRatingUnitRequest} from '../../model/sure-flow-rating-unit-request';
import {SureFlowRatingCoilRequest} from '../../model/sure-flow-rating-coil-request';
import {SureFlowUnitService} from '../../services/sure-flow-unit.service';
import {RatingService} from '../../services/rating.service';
import {RatingUnitUtils} from '../../utils/rating-unit-utils';
import {SureFlowRatingUnitResponse} from '../../model/sure-flow-rating-unit-response';
import {Sku} from '../../model/sku';
import {IecLeadTime} from '../../model/iec-lead-time';
import {SfrManagementService} from '../../services/sfr-management-service';
import {SfrEntry} from '../../model/sfr-entry';
import {LeadTimeService} from '../../services/lead-time.service';
import {GuruAllowable} from '../../model/guru-allowable';
import {UnitConversionUtils} from '../../configurator/unit-conversion-utils';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {SkusAndPricingSectionComponent} from './skus-and-pricing-section/skus-and-pricing-section.component';
import {VerticalFloorACabinetFeaturesSectionComponent} from './vertical-floor-a-cabinet-features-section/vertical-floor-a-cabinet-features-section.component';
import {CabinetFeaturesSectionComponent} from './cabinet-features-section/cabinet-features-section.component';
import {SecondaryValvePackageSectionComponent} from './secondary-valve-package-section/secondary-valve-package-section.component';
import {PrimaryValvePackageSectionComponent} from './primary-valve-package-section/primary-valve-package-section.component';
import {CoilOptionsSectionComponent} from './coil-options-section/coil-options-section.component';
import {ControlsSectionComponent} from './controls-section/controls-section.component';
import {ModularMechanicalConnectionsSectionComponent} from './modular-mechanical-connections-section/modular-mechanical-connections-section.component';
import {MechanicalConnectionsSectionComponent} from './mechanical-connections-section/mechanical-connections-section.component';
import {PerformanceResultsSectionComponent} from './performance-results-section/performance-results-section.component';
import {PerformanceInputsSectionComponent} from './performance-inputs-section/performance-inputs-section.component';
import {IndoorAirQualitySectionComponent} from './indoor-air-quality-section/indoor-air-quality-section.component';
import {RatingsSectionComponent} from './ratings-section/ratings-section.component';
import {UnitDetailsSectionComponent} from './unit-details-section/unit-details-section.component';

import {MatIcon} from '@angular/material/icon';
import {MatButton} from '@angular/material/button';

@Component({
    selector: 'app-sureflow-entity-unit-configurator',
    templateUrl: './sureflow-entity-unit-configurator.component.html',
    styleUrls: ['./sureflow-entity-unit-configurator.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButton, MatIcon, UnitDetailsSectionComponent, RatingsSectionComponent, IndoorAirQualitySectionComponent, PerformanceInputsSectionComponent, PerformanceResultsSectionComponent, MechanicalConnectionsSectionComponent, ModularMechanicalConnectionsSectionComponent, ControlsSectionComponent, CoilOptionsSectionComponent, PrimaryValvePackageSectionComponent, SecondaryValvePackageSectionComponent, CabinetFeaturesSectionComponent, VerticalFloorACabinetFeaturesSectionComponent, SkusAndPricingSectionComponent, MatProgressSpinner]
})
export class SureflowEntityUnitConfiguratorComponent implements OnInit {
  private readonly configurationService = inject(SureFlowConfigurationService);
  private readonly unitSelectionService = inject(UnitSelectionService);
  private readonly router = inject(Router);
  private readonly dialogService = inject(DialogService);
  private readonly modelService = inject(ModelService);
  private readonly entityLineService = inject(EntityLineService);
  private readonly unitService = inject(SureFlowUnitService);
  private readonly ratingService = inject(RatingService);
  private readonly sfrManagementService = inject(SfrManagementService);
  private readonly leadTimeService = inject(LeadTimeService);


  private readonly ratingsModels = signal<RatingsModel[]>(undefined!);
  private readonly systemTypes = signal<SelectionSystemType[]>(undefined!);
  private readonly selectedRatingsModel = signal<RatingsModel>(undefined!);
  readonly affectedLines = signal<EntityLine[]>(undefined!);
  readonly handArrangementImagePath = signal<string>(undefined!);
  readonly pipeDetailImagePath = signal<string>(undefined!);
  readonly entity = signal<Entity>(undefined!);
  readonly entityName = signal<string>(undefined!);
  readonly lastRatedUnit = signal<EntitySureFlowUnit>(undefined!);
  readonly selectedLoop = signal<EntitySureFlowLoop>(undefined!);
  readonly selectedUnits = signal<EntitySureFlowUnit[]>(undefined!);
  readonly engineKey = signal<string>(undefined!);
  readonly characteristics = signal<SpecificationCharacteristic[]>(undefined!);
  readonly selections = signal<GuruEntity[]>(undefined!);
  readonly invalidEntities = signal<string[]>([]);
  readonly messages = signal<string[]>([]);
  readonly addMode = signal<boolean>(undefined!);
  readonly singleLineMode = signal<boolean>(undefined!);
  readonly selectedProductId = signal<string>(undefined!);
  tagInformation!: TagInformation[];
  readonly isBusy = signal<boolean>(undefined!);
  readonly errorMessage = signal<string>(undefined!);
  readonly ratingResponse = signal<SureFlowRatingUnitResponse>(undefined!);
  readonly skus = signal<Sku[]>(undefined!);
  readonly sfrs = signal<SfrEntry[]>(undefined!);
  readonly slowestLeadTime = signal<IecLeadTime>(undefined!);
  readonly entityLeadTime = signal<IecLeadTime>(undefined!);
  readonly configurationLeadTime = signal<IecLeadTime>(undefined!);
  readonly showSkusAndPrices = signal<boolean>(undefined!);
  readonly dataLoaded = signal<boolean>(false);
  readonly ratingUnitData = signal<SureFlowUnitData>(undefined!);

  changeProducts(): void {
    if (this.canChangeProducts()) {
      this.selections.set([]);
      this.dataLoaded.set(false);
      this.isBusy.set(true);
      this.modelService.removeEngine(this.engineKey()).subscribe({
        next: () => {
          this.messages.set([]);
          this.skus.set([]);
          this.sfrs.set([]);
          this.invalidEntities.set([]);
          this.selectedProductId.set(undefined!);
          this.engineKey.set(undefined!);
          this.selectedRatingsModel.set(undefined!);
          this.configurationService.clearSelectedProductId();
          this.configurationService.clearLastRatedUnit();
          this.ngOnInit();
        }, error: (error) => this.handleError(error)
      })
    }
  }

  canChangeProducts(): boolean {
    return !this.isBusy() && StringUtils.isNotBlank(this.engineKey()) && this.addMode();
  }

  ngOnInit(): void {
    this.isBusy.set(true);
    this.entity.set(this.configurationService.getSelectedEntity());
    this.entityName.set(this.configurationService.getEntityName());
    this.lastRatedUnit.set(this.configurationService.getLastRatedUnit());
    this.selectedLoop.set(this.configurationService.getSelectedEntityLoop());
    this.selectedUnits.set(this.configurationService.getSelectedEntityUnits());
    if (StringUtils.isBlank(this.entity()?.id?.entityId) || StringUtils.isBlank(this.entityName()) || StringUtils.isEmptyNumericValue(this.selectedLoop()?.id)) {
      this.backToSureFlowLoopsList();
    } else {
      this.loadStaticSelectionData().subscribe({
        next: () => {
          this.loadAffectedLines().then(lines => {
            this.affectedLines.set(lines);
            if (this.affectedLines().length === 0) {
              this.addMode.set(true);
              this.selectedUnits.set([]);
              this.selectProduct().subscribe({
                complete: () => {
                  if (StringUtils.isBlank(this.selectedProductId())) {
                    this.backToEntityLoops();
                  } else {
                    this.selectedRatingsModel.set(this.ratingsModels().filter(model => model.productId === this.selectedProductId())[0]);
                    this.configurationService.setSelectedProductId(this.selectedProductId());
                    this.loadProductEngine().subscribe({
                      next: (engineKey: string) => {
                        if (StringUtils.isBlank(engineKey)) {
                          this.backToEntityLoops();
                        } else {
                          this.engineKey.set(engineKey);
                          this.selectedUnits.update(current => [...current, this.initializeUnit()]);
                          this.showSkusAndPrices.set(!this.selectedLoop().schedule);
                          this.initializeRatingUnitData();
                          this.setUnitValuesFromEngine().then(() => {
                            this.getRatingResponse().subscribe({
                              next: (response: SureFlowRatingUnitResponse) => this.ratingResponse.set(response),
                              error: (error) => this.handleError(error),
                              complete: () => {
                                this.isBusy.set(false);
                                this.dataLoaded.set(true);
                              }
                            });
                          }, (error) => this.handleError(error));
                        }
                      }, error: (error) => this.handleError(error)
                    });
                  }
                }, error: (error) => this.handleError(error)
              });
            } else if (this.affectedLines().length === 1) {
              this.showSkusAndPrices.set(this.isSingleUnitMode() && !this.selectedLoop().schedule);
              this.singleLineMode.set(true);
              const productId: string = this.selectedUnits()[0].unitData.sureFlowRatingUnit.productId;
              this.configurationService.setSelectedProductId(productId);
              this.selectedProductId.set(productId);
              this.initializeRatingUnitData();
              this.selectedRatingsModel.set(this.ratingsModels().filter(model => model.productId === this.selectedProductId())[0]);
              this.loadProductEngine().subscribe({
                next: (engineKey: string) => {
                  if (StringUtils.isBlank(engineKey)) {
                    this.backToEntityLoops();
                  } else {
                    this.engineKey.set(engineKey);
                    this.setUnitValuesFromEngine().then(() => {
                      this.getRatingResponse().subscribe({
                        next: (response: SureFlowRatingUnitResponse) => this.ratingResponse.set(response),
                        error: (error) => this.handleError(error),
                        complete: () => {
                          this.isBusy.set(false);
                          this.dataLoaded.set(true);
                        }
                      });
                    }, (error) => this.handleError(error));
                  }
                }, error: (error) => this.handleError(error)
              })
            } else {
              this.showSkusAndPrices.set(false);
              this.initializeRatingUnitData();
              this.loadCommonCharacteristics().then(() => {
                this.isBusy.set(false);
                this.dataLoaded.set(true);
              }, (error) => this.handleError(error));
            }
          }, error => this.handleError(error));
        }, error: (error) => this.handleError(error)
      });
    }
  }

  private initializeRatingUnitData(): void {
    this.ratingUnitData.set(this.selectedUnits()[0].unitData);
    if (StringUtils.isBlank(this.ratingUnitData()?.sureFlowRatingUnit?.request?.fanSpeed)) {
      this.ratingUnitData().sureFlowRatingUnit.request.fanSpeed = 'H';
    }
    const systemType: SelectionSystemType = this.getSelectedSystemType();
    this.ratingUnitData().sureFlowRatingUnit.request.primaryCoilRequest.isCooling = StringUtils.equalsIgnoreCase('C', systemType.primaryCoilApplication);
    if (!this.isSingleUnitMode()) {
      this.ratingUnitData().coolingEnteringFluidTemperature = undefined!;
      this.ratingUnitData().heatingEnteringFluidTemperature = undefined!;
    }
  }

  private getRatingResponse(): Observable<SureFlowRatingUnitResponse> {
    const subject: Subject<SureFlowRatingUnitResponse> = new Subject<SureFlowRatingUnitResponse>();
    if (this.isSingleUnitMode() && this.ratingUnitData()) {
      const ratingUnit = this.ratingUnitData().sureFlowRatingUnit;
      if (ratingUnit) {
        const request = RatingUnitUtils.convertSureFlowRatingRequest(ratingUnit.request);
        this.ratingService.getUnitRatingResponse(request).subscribe({
          next: (response) => subject.next(RatingUnitUtils.convertRatingResponse(response)),
          error: (error) => subject.error(error),
          complete: () => subject.complete()
        });
      } else {
        subject.error('Cannot get a rating response without a rating unit');
      }
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  isSingleUnitMode(): boolean {
    return this.addMode() || (this.selectedUnits()?.length || 0) === 1;
  }

  private initializeUnit(): EntitySureFlowUnit {
    const unit = new EntitySureFlowUnit();
    unit.loopId = this.selectedLoop().id;
    unit.sequence = 999;
    const systemType = this.getSelectedSystemType();
    if (this.lastRatedUnit() && this.selectedProductId() === (this.lastRatedUnit().unitData?.sureFlowRatingUnit?.productId || '')) {
      unit.unitData = JSON.parse(JSON.stringify(this.lastRatedUnit().unitData));
      RatingUnitUtils.clearSureFlowRatingIds(unit.unitData);
      this.setSelection('USUnit', this.lastRatedUnit().unitData.sureFlowRatingUnit.modelCode);
      this.setSelection('USSize', this.lastRatedUnit().unitData.sureFlowRatingUnit.sizeCode);
      this.setSelection('USCoilRows', this.lastRatedUnit().unitData.sureFlowRatingUnit.coilRowCode);
      this.setSelection('USUnitVoltage', this.lastRatedUnit().unitData.sureFlowRatingUnit.powerSupplyCode);
      this.setSelection('USElecHtKw', this.lastRatedUnit().unitData.sureFlowRatingUnit.electricHeatCode);
      this.setSelection('USMotorType', this.lastRatedUnit().unitData.sureFlowRatingUnit.motorTypeCode);
    } else {
      unit.unitData = new SureFlowUnitData();
      unit.unitData.active = true;
      unit.unitData.sureFlowRatingUnit = new SureFlowRatingUnit();
      unit.unitData.sureFlowRatingUnit.productId = this.selectedProductId();
      unit.unitData.sureFlowRatingUnit.request = new SureFlowRatingUnitRequest();
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest = new SureFlowRatingCoilRequest();
      if (StringUtils.equalsIgnoreCase('H', systemType?.secondaryCoilApplication)) {
        unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest = new SureFlowRatingCoilRequest();
      }
      if (systemType?.electricHeat) {
        unit.unitData.sureFlowRatingUnit.request.electricHeatRequest = new SureFlowRatingCoilRequest();
      }
    }
    if (StringUtils.equalsIgnoreCase('C', systemType.primaryCoilApplication)) {
      let dryBulb: number = this.selectedLoop().detail.metric ? this.round(UnitConversionUtils.convertTemperatureValue(75, true)) : 75;
      let wetBulb: number = this.selectedLoop().detail.metric ? this.round(UnitConversionUtils.convertTemperatureValue(63, true)) : 63;
      if (this.lastRatedUnit()?.unitData?.sureFlowRatingUnit?.request?.primaryCoilRequest) {
        dryBulb = this.lastRatedUnit().unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb;
        wetBulb = this.lastRatedUnit().unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringWetBulb;
      }
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.fluidType = this.selectedLoop().detail.coolingFluidTypeCode;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.glycolPercentage = this.selectedLoop().detail.coolingGlycolPercentage;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringFluidTemperature = this.selectedLoop().detail.coolingLeavingFluidTemperature || this.selectedLoop().detail.coolingEnteringFluidTemperature;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = dryBulb;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringWetBulb = wetBulb;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.isCooling = true;
    } else {
      let dryBulb = this.selectedLoop().detail.metric ? this.round(UnitConversionUtils.convertTemperatureValue(70, true)) : 70;
      if (this.lastRatedUnit()?.unitData?.sureFlowRatingUnit?.request?.primaryCoilRequest) {
        dryBulb = this.lastRatedUnit().unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb;
      }
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.fluidType = this.selectedLoop().detail.heatingFluidTypeCode;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.glycolPercentage = this.selectedLoop().detail.heatingGlycolPercentage;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringFluidTemperature = this.selectedLoop().detail.heatingLeavingFluidTemperature || this.selectedLoop().detail.heatingEnteringFluidTemperature;
      unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = dryBulb;
    }
    if (StringUtils.equalsIgnoreCase('H', systemType?.secondaryCoilApplication)) {
      let dryBulb = this.selectedLoop().detail.metric ? this.round(UnitConversionUtils.convertTemperatureValue(70, true)) : 70;
      if (this.lastRatedUnit()?.unitData?.sureFlowRatingUnit?.request?.secondaryCoilRequest) {
        dryBulb = this.lastRatedUnit().unitData.sureFlowRatingUnit.request.secondaryCoilRequest.enteringDryBulb;
      }
      unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.fluidType = this.selectedLoop().detail.heatingFluidTypeCode;
      unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.glycolPercentage = this.selectedLoop().detail.heatingGlycolPercentage;
      unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.enteringFluidTemperature = this.selectedLoop().detail.heatingLeavingFluidTemperature || this.selectedLoop().detail.heatingEnteringFluidTemperature;
      unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.enteringDryBulb = dryBulb;
    }
    if (unit.unitData?.sureFlowRatingUnit?.request?.electricHeatRequest) {
      let dryBulb = this.selectedLoop().detail.metric ? this.round(UnitConversionUtils.convertTemperatureValue(70, true)) : 70;
      if (this.lastRatedUnit()?.unitData?.sureFlowRatingUnit?.request?.electricHeatRequest) {
        dryBulb = this.lastRatedUnit().unitData.sureFlowRatingUnit.request.electricHeatRequest.enteringDryBulb;
      }
      unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.enteringDryBulb = dryBulb;
      unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.isElectricHeat = true;
      unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.isCooling = false;
    }
    return unit;
  }

  private setSelection(entityName: string, value: string): void {
    if (StringUtils.isNotBlank(value)) {
      const entity: GuruEntity = new GuruEntity();
      entity.Name = entityName;
      entity.Value = value;
      this.selectionChanged(entity);
    }
  }

  private round(value: string): number {
    const numericValue = +value;
    const roundedValue = numericValue.toFixed(3);
    return +roundedValue;
  }

  private getSelectedSystemType(): SelectionSystemType {
    if (this.systemTypes()) {
      return this.systemTypes().filter(systemType => systemType.code === this.selectedLoop().detail.systemTypeCode)[0];
    }
    return undefined!;
  }

  private loadStaticSelectionData(): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.unitSelectionService.getSureFlowModels().subscribe({
      next: (ratingsModels) => {
        this.ratingsModels.set(ratingsModels);
        this.unitSelectionService.getSureFlowSystemTypes().subscribe({
          next: (systemTypes) => {
            this.systemTypes.set(systemTypes);
            subject.next();
            subject.complete();
          }, error: (error) => subject.error(error)
        })
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private loadProductEngine(): Observable<string> {
    const subject: Subject<string> = new Subject<string>();
    if (StringUtils.isNotBlank(this.selectedProductId())) {
      this.leadTimeService.getLeadTimeByProgram(this.entity().leadTimeCode).subscribe({
        next: (leadTime) => {
          this.entityLeadTime.set(leadTime);
          this.leadTimeService.getSlowestLeadTime(this.entity().brandID).subscribe({
            next: (slowestLeadTime) => {
              this.slowestLeadTime.set(slowestLeadTime);
              this.modelService.getEngineKey(this.selectedProductId()).subscribe({
                next: (engineKey: string) => {
                  this.setLeadTimeCodeInEngine(engineKey).subscribe({
                    next: () => {
                      if (this.singleLineMode()) {
                        const line: EntityLine = this.affectedLines()[0];
                        const specifications: Specification[] = line.singleEntityLines[0].specifications;
                        const characteristics: SpecificationCharacteristic[] = specifications.flatMap(spec => spec.specificationCharacteristics);
                        this.modelService.loadConfiguration(engineKey, characteristics).subscribe({
                          next: () => {
                            let airflow: string = '0';
                            if (this.isSingleUnitMode()) {
                              const unit = this.selectedUnits()[0];
                              if ('C' === (unit.unitData?.sureFlowRatingUnit?.request?.fanSpeed || '')) {
                                airflow = (unit.unitData?.sureFlowRatingUnit?.request?.airflow || 0).toString();
                              }
                            }
                            this.modelService.setValue('USFAirflow', engineKey, airflow).subscribe({
                              next: () => {
                                this.modelService.setValue('PRN01', engineKey, this.entity().prnField1).subscribe({
                                  next: () => {
                                    this.modelService.executeHierarchy(engineKey, 'hierModelAlias').subscribe({
                                      next: () => {
                                        this.checkEngineValidity(engineKey).subscribe({
                                          next: () => subject.next(engineKey),
                                          error: (error) => subject.error(error),
                                          complete: () => subject.complete()
                                        });
                                      }, error: (error) => subject.error(error)
                                    });
                                  }, error: (error) => subject.error(error)
                                });
                              }, error: (error) => subject.error(error)
                            });
                          }, error: (error) => subject.error(error)
                        });
                      } else if (this.addMode()) {
                        this.modelService.setValue('USUnit', engineKey, this.selectedRatingsModel().modelCode).subscribe({
                          next: () => {
                            this.modelService.setValue('PRN01', engineKey, this.entity().prnField1).subscribe({
                              next: () => {
                                this.modelService.setValue('USSystemType', engineKey, this.selectedLoop().detail.systemTypeCode).subscribe({
                                  next: () => {
                                    this.modelService.executeHierarchy(engineKey, 'hierSystemTypeDefaults').subscribe({
                                      next: () => {
                                        this.modelService.executeHierarchy(engineKey, 'hierModelDefaults').subscribe({
                                          next: () => {
                                            this.modelService.executeHierarchy(engineKey, 'hierModelAlias').subscribe({
                                              next: () => {
                                                this.modelService.executeHierarchy(engineKey, 'hierSKU').subscribe({
                                                  next: () => {
                                                    this.modelService.executeHierarchy(engineKey, 'hierPRN').subscribe({
                                                      next: () => {
                                                        this.modelService.executeHierarchy(engineKey, 'hierDOC').subscribe({
                                                          next: () => {
                                                            this.checkEngineValidity(engineKey).subscribe({
                                                              next: () => subject.next(engineKey),
                                                              error: (error) => subject.error(error)
                                                            });
                                                          }, error: (error) => this.handleError(error)
                                                        });
                                                      }, error: (error) => subject.error(error)
                                                    });
                                                  }, error: (error) => subject.error(error)
                                                });
                                              }, error: (error) => subject.error(error)
                                            });
                                          }, error: (error) => subject.error(error)
                                        });
                                      }, error: (error) => subject.error(error)
                                    });
                                  }, error: (error) => subject.error(error)
                                });
                              }, error: (error) => this.handleError(error)
                            });
                          }, error: (error) => this.handleError(error)
                        });
                      } else {
                        subject.error('Unable to load product selections');
                      }
                    }, error: (error) => subject.error(error)
                  });
                }, error: (error) => subject.error(error)
              });
            }, error: (error) => subject.error(error)
          });
        }, error: (error) => this.handleError(error),
      });
    } else {
      subject.error('Cannot load an engine without a product id');
    }
    return subject.asObservable();
  }

  private setLeadTimeCodeInEngine(engineKey: string): Observable<string> {
    const subject: Subject<string> = new Subject<string>();
    this.modelService.getEntitiesStartingWithName(engineKey, 'USLeadTime').subscribe({
      next: (entities) => {
        if ((entities?.length || 0) > 0) {
          const leadTimeCode: string = this.entityLeadTime()?.code || 'REG';
          this.modelService.setValue('USLeadTime', engineKey, leadTimeCode).subscribe({
            next: () => {
              this.modelService.executeHierarchy(engineKey, 'hierDefaultOnce').subscribe({
                next: () => subject.next('USLeadTime set to ' + leadTimeCode),
                error: (error) => subject.error(error)
              });
            }, error: (error) => subject.error(error)
          });
        } else {
          this.modelService.executeHierarchy(engineKey, 'hierDefaultOnce').subscribe({
            next: () => subject.next('USLeadTime NOT set'),
            error: (error) => subject.error(error)
          });
        }
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private selectProduct(): Observable<string> {
    const subject: Subject<string> = new Subject<string>();
    const selectedProductId: string = this.getSelectedProductId();
    if (StringUtils.isNotBlank(selectedProductId)) {
      this.selectedProductId.set(selectedProductId);
      subject.next(selectedProductId);
      subject.complete();
    } else {
      this.dialogService.showProductSelectionDialog(this.ratingsModels()).subscribe({
        next: (product: RatingsModel) => {
          this.selectedProductId.set(product?.productId);
          subject.next(product?.productId);
          subject.complete();
        }, error: (error) => subject.error(error)
      });
    }
    return subject.asObservable();
  }

  private getSelectedProductId(): string {
    const selectedProductId: string = this.configurationService.getSelectedProductId();
    if (StringUtils.isNotBlank(selectedProductId)) {
      return selectedProductId;
    } else if (StringUtils.isNotBlank(this.lastRatedUnit()?.unitData?.sureFlowRatingUnit?.productId)) {
      return this.lastRatedUnit().unitData.sureFlowRatingUnit.productId;
    } else {
      return undefined!;
    }
  }

  private handleError(error: any): void {
    this.isBusy.set(false);
    this.errorMessage.set(StringUtils.formatErrorMessage(error));
  }

  private async loadCommonCharacteristics(): Promise<string> {
    this.characteristics.set([]);
    this.selections.set(await this.loadCommonSelections());
    for (const selection of this.selections()) {
      selection.Allowables.forEach(allowable => allowable.IsValid = true);
    }
    return 'Common characteristics loaded';
  }

  private async loadCommonSelections(): Promise<GuruEntity[]> {
    let commonSelections: GuruEntity[] = [];
    for (const entityLine of this.affectedLines()) {
      const lineSelections: GuruEntity[] = await this.getLineSelections(entityLine);
      if (commonSelections.length === 0) {
        commonSelections = JSON.parse(JSON.stringify(lineSelections));
      } else {
        const lineSelectionNames: string[] = [];
        for (const lineSelection of lineSelections) {
          const allowableValues: string[] = lineSelection.Allowables.map(allowable => allowable.Value);
          const filteredCommon = commonSelections.filter(cs => cs.Name === lineSelection.Name);
          if (filteredCommon.length === 1) {
            filteredCommon[0].Allowables = filteredCommon[0].Allowables.filter(allowable => allowableValues.indexOf(allowable.Value) >= 0);
            if (filteredCommon[0].Allowables.length > 0) {
              lineSelectionNames.push(lineSelection.Name);
            }
          }
        }
        commonSelections = commonSelections.filter(selection => lineSelectionNames.indexOf(selection.Name) >= 0);
      }
    }
    return commonSelections;
  }

  private async getLineSelections(entityLine: EntityLine): Promise<GuruEntity[]> {
    const productId = entityLine.product.productId;
    const engineKey: string = await firstValueFrom<string>(this.modelService.getEngineKey(productId));
    const specifications: Specification[] = entityLine.singleEntityLines[0].specifications;
    const characteristics: SpecificationCharacteristic[] = specifications.flatMap(spec => spec.specificationCharacteristics);
    await firstValueFrom<string>(this.modelService.loadConfiguration(engineKey, characteristics));
    const entities: GuruEntity[] = await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(engineKey, 'hierUserSelections'));
    entities.forEach(entity => entity.Allowables.forEach(allowable => allowable.IsValid = true));
    await firstValueFrom<string>(this.modelService.removeEngine(engineKey));
    return entities;
  }

  private async loadAffectedLines(): Promise<EntityLine[]> {
    const affectedLines: EntityLine[] = [];
    const selectedLineNumbers = new Set(this.selectedUnits().filter(line => StringUtils.isNotBlank(line.entityLineId)).map(line => line.entityLineId));
    for (const lineNumber of selectedLineNumbers) {
      const filteredLines = this.entity().lines.filter(line => line.lineId === lineNumber);
      if (filteredLines.length === 1) {
        const fullEntityLine: EntityLine = await this.getFullEntityLine(filteredLines[0]);
        affectedLines.push(fullEntityLine);
      }
    }
    return affectedLines;
  }

  private async getFullEntityLine(line: EntityLineHeader): Promise<EntityLine> {
    return await firstValueFrom<EntityLine>(this.entityLineService.getFullEntityLine(line));
  }

  backToSureFlowLoopsList(): void {
    this.router.navigate(['ratings', 'sureflowLoops']);
  }

  backToEntityLoops(): void {
    this.configurationService.setSelectedProductId(undefined!);
    this.configurationService.setSelectedEntityUnits(undefined!);
    this.configurationService.setEntityName(undefined!);
    this.configurationService.setSelectedEntity(undefined!);
    if (StringUtils.isBlank(this.entity()?.id?.entityId) || StringUtils.isBlank(this.entityName())) {
      this.router.navigate(['main']);
    } else {
      this.router.navigate([this.entityName(), 'sureflow', 'loops', this.entity().id.entityId, this.entity().id.entityRevision]);
    }
  }

  selectionChanged(updatedEntity: GuruEntity): void {
    this.errorMessage.set(undefined!);
    if (StringUtils.isNotBlank(this.engineKey())) {
      this.isBusy.set(true);
      this.messages.set([]);
      this.invalidEntities.set([]);
      this.modelService.setValue(updatedEntity.Name, this.engineKey(), updatedEntity.Value).subscribe({
        next: () => {
          this.setSelectionValue(updatedEntity);
          if ('USUnit' === updatedEntity.Name) {
            this.selectedRatingsModel.set(this.ratingsModels().filter(model => model.modelCode === updatedEntity.Value)[0]);
            this.modelService.executeHierarchy(this.engineKey(), 'hierModelDefaults').subscribe({
              next: () => {
                this.updateUnitRatingsWithoutSettingEntityValue();
              }, error: (error) => this.handleError(error)
            });
          } else {
            this.updateUnitRatingsWithoutSettingEntityValue();
          }
        }, error: (error) => this.handleError(error)
      });
    } else {
      this.updateSelectedUnits(updatedEntity);
    }
  }

  private setSelectionValue(entity: GuruEntity): void {
    for (const selection of this.selections()) {
      if (selection.Name === entity.Name) {
        selection.Value = entity.Value;
      }
    }
  }

  private updateUnitRatingsWithoutSettingEntityValue() {
    this.setUnitValuesFromEngine().then(() => {
      this.checkEngineValidity(this.engineKey()).subscribe({
        next: () => {
          this.getRatingResponse().subscribe({
            next: (response: SureFlowRatingUnitResponse) => this.ratingResponse.set(response),
            error: (error) => this.handleError(error),
            complete: () => this.isBusy.set(false)});
        }, error: (error) => this.handleError(error)
      });
    }, (error) => this.handleError(error));
  }

  updateUnitRatings(entity: GuruEntity): void {
    if (StringUtils.isNotBlank(this.engineKey())) {
      this.isBusy.set(true);
      this.ratingResponse.set(undefined!);
      this.modelService.setValue(entity.Name, this.engineKey(), entity.Value).subscribe({
        next: () => {
          this.setSelectionValue(entity);
          this.updateUnitRatingsWithoutSettingEntityValue();
        }, error: (error) => this.handleError(error)
      });
    } else {
      this.updateSelectedUnits(entity);
    }
  }

  targetLoadChanged(): void {
    if (StringUtils.isNotBlank(this.engineKey())) {
      this.updateUnitRatingsWithoutSettingEntityValue();
    }
  }

  private updateSelectedUnits(entity: GuruEntity): void {
    const systemType = this.getSelectedSystemType();
    const filteredCharacteristics = this.characteristics().filter(characteristic => characteristic.key === entity.Name);
    if (filteredCharacteristics.length === 0) {
      const characteristic: SpecificationCharacteristic = new SpecificationCharacteristic();
      characteristic.id = '';
      characteristic.key = entity.Name;
      characteristic.value = entity.Value;
      characteristic.tags = [];
      this.characteristics.update(current => [...current, characteristic]);
    } else {
      filteredCharacteristics[0].value = entity.Value;
    }
    for (const unit of this.selectedUnits()) {
      switch (entity.Name) {
        case 'USSize':
          unit.unitData.sureFlowRatingUnit.sizeCode = entity.Value;
          unit.unitData.sureFlowRatingUnit.request.size = entity.Value;
          break;
        case 'USFESP':
          unit.unitData.sureFlowRatingUnit.request.externalStaticPressure = +entity.Value;
          break;
        case 'USFSpeed':
          unit.unitData.sureFlowRatingUnit.request.fanSpeed = entity.Value;
          break;
        case 'USAltitude':
          unit.unitData.sureFlowRatingUnit.request.altitude = +entity.Value;
          break;
        case 'USFAirflow':
          unit.unitData.sureFlowRatingUnit.request.airflow = +entity.Value;
          break;
        case 'USCEDB':
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = +entity.Value;
          break;
        case 'USCEWB':
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringWetBulb = +entity.Value;
          break;
        case 'USHEDB':
          if (StringUtils.equalsIgnoreCase('H', systemType?.primaryCoilApplication)) {
            unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = +entity.Value;
          } else if (StringUtils.equalsIgnoreCase('H', systemType?.secondaryCoilApplication)) {
            unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.enteringDryBulb = +entity.Value;
          }
          if (systemType?.electricHeat) {
            unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.enteringDryBulb = +entity.Value;
          }
          break;
      }
    }
  }

  private async setUnitValuesFromEngine(): Promise<string> {
    if (this.engineKey()) {
      for (const unit of this.selectedUnits()) {
        unit.unitData.sureFlowRatingUnit.airFilterCode = this.getSelectionValue('USFilter');
        unit.unitData.sureFlowRatingUnit.coilRowCode = this.getSelectionValue('USCoilRows');
        unit.unitData.sureFlowRatingUnit.productId = this.selectedProductId();
        unit.unitData.sureFlowRatingUnit.electricHeatCode = this.getSelectionValue('USElecHtKw');
        let modelCode: string = this.getSelectionValue('USUnit');
        if (StringUtils.isBlank(modelCode)) {
          modelCode = this.selectedRatingsModel().modelCode;
          await firstValueFrom<string>(this.modelService.setValue('USUnit', this.engineKey(), modelCode));
          await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey(), 'hierModelDefaults'));
        }
        unit.unitData.sureFlowRatingUnit.modelCode = modelCode;
        unit.unitData.sureFlowRatingUnit.motorTypeCode = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USMotorType', 'Code'));
        unit.unitData.sureFlowRatingUnit.powerSupplyCode = this.getSelectionValue('USUnitVoltage');
        let sizeCode = this.getSelectionValue('USSize');
        if (StringUtils.isBlank(sizeCode)) {
          const sizeEntity: GuruEntity = await firstValueFrom<GuruEntity>(this.modelService.getEntity(this.engineKey(), 'USSize'));
          sizeCode = sizeEntity.Allowables[0].Value;
          await firstValueFrom<string>(this.modelService.setValue('USSize', this.engineKey(), sizeCode));
        }
        unit.unitData.sureFlowRatingUnit.sizeCode = sizeCode;
        unit.unitData.sureFlowRatingUnit.request.externalStaticPressure = +this.getSelectionValue('USFESP');
        unit.unitData.sureFlowRatingUnit.request.motorVoltage = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USUnitVoltage', 'Description'));
        unit.unitData.sureFlowRatingUnit.request.fanSpeed = this.getSelectionValue('USFSpeed');
        unit.unitData.sureFlowRatingUnit.request.filter = unit.unitData.sureFlowRatingUnit.airFilterCode;
        unit.unitData.sureFlowRatingUnit.request.size = sizeCode;
        unit.unitData.sureFlowRatingUnit.request.model = modelCode;
        unit.unitData.sureFlowRatingUnit.request.altitude = +this.getSelectionValue('USAltitude');
        unit.unitData.sureFlowRatingUnit.request.airflow = +this.getSelectionValue('USFAirflow');
        unit.unitData.sureFlowRatingUnit.request.motorType = unit.unitData.sureFlowRatingUnit.motorTypeCode;
        const systemType = this.getSelectedSystemType();
        const primaryRowCount: string = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USCoilRows', 'PriRows'));
        if (StringUtils.equalsIgnoreCase('C', systemType?.primaryCoilApplication)) {
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.isCooling = true;
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = +this.getSelectionValue('USCEDB');
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringWetBulb = +this.getSelectionValue('USCEWB');
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.rowCount = +primaryRowCount;
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.isCooling = true;
        } else if (StringUtils.equalsIgnoreCase('H', systemType?.primaryCoilApplication)) {
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.isCooling = false;
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = +this.getSelectionValue('USHEDB');
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.rowCount = +primaryRowCount;
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.isCooling = false;
        }
        if (StringUtils.equalsIgnoreCase('H', systemType?.secondaryCoilApplication)) {
          const secondaryRowCount: string = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USCoilRows', 'SecRows'));
          unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.isCooling = false;
          unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.enteringDryBulb = +this.getSelectionValue('USHEDB');
          unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.rowCount = +secondaryRowCount;
          unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.isCooling = false;
        }
        if (systemType?.electricHeat) {
          if (!unit.unitData.sureFlowRatingUnit.request.electricHeatRequest) {
            unit.unitData.sureFlowRatingUnit.request.electricHeatRequest = new SureFlowRatingCoilRequest();
          }
          unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.enteringDryBulb = +this.getSelectionValue('USHEDB');
          unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.electricHeatVoltage = unit.unitData.sureFlowRatingUnit.request.motorVoltage;
          const kilowattValue = await firstValueFrom<string>(this.modelService.getCharacteristicValue(this.engineKey(), 'USElecHtKw', 'Kilowatts'));
          unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.electricHeatKilowatts = +kilowattValue;
          unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.isElectricHeat = true;
          unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.isCooling = false;
        }
      }
    } else {
      return Promise.reject('Cannot set unit values from engine without an engine key');
    }
    return 'Success';
  }

  private getSelectionValue(selectionName: string): string {
    if ((this.selections()?.length || 0) > 0) {
      const filtered = this.selections().filter(selection => StringUtils.equalsIgnoreCase(selectionName, selection.Name));
      if (filtered.length === 1) {
        return filtered[0].Value;
      }
    }
    return undefined!;
  }

  getProductImage(): string {
    return this.selectedRatingsModel()?.imageFileName;
  }

  private getImagePrefix(): string {
    if (StringUtils.isNotBlank(this.selectedRatingsModel()?.imageFileName)) {
      return this.selectedRatingsModel().imageFileName.substring(0, this.selectedRatingsModel().imageFileName.lastIndexOf('/') + 1);
    }
    return '';
  }

  isModularProductSelected(): boolean {
    return StringUtils.equalsIgnoreCase('MegaMod-Sureflow', this.selectedProductId())
      || StringUtils.equalsIgnoreCase('Modular-Sureflow', this.selectedProductId());
  }

  isVerticalFloorA(): boolean {
    return StringUtils.equalsIgnoreCase('VerticalFloorA-Sureflow', this.selectedProductId());
  }

  private checkEngineValidity(engineKey: string): Observable<void> {
    const subject: Subject<void> = new Subject<void>();
    this.loadSkusAndPrices(engineKey);
    this.modelService.executeHierarchy(engineKey, 'hierReset').subscribe({
      next: () => {
        this.modelService.executeHierarchy(engineKey, 'hierDOC').subscribe({
          next: () => {
            this.modelService.getCharacteristicValue(engineKey, 'UnitHandArrangementImage', 'FileName').subscribe({
              next: (result) => {
                this.handArrangementImagePath.set(this.getImagePrefix() + result);
                if (this.isModularProductSelected()) {
                  this.modelService.getCharacteristicValue(engineKey, 'PipeDetailImage', 'FileName').subscribe({
                    next: (result) => this.pipeDetailImagePath.set(this.getImagePrefix() + result),
                    error: (error) => this.handleError(error)
                  });
                }
                this.modelService.executeHierarchy(engineKey, 'hierUserSelections').subscribe({
                  next: (entities) => {
                    this.selections.set(entities);
                    this.selectFirstValidOptions(engineKey).subscribe({
                      complete: () => {
                        this.modelService.isComplete(engineKey).subscribe({
                          next: (complete: boolean) => {
                            this.modelService.isValid(engineKey).subscribe({
                              next: (valid: boolean) => {
                                if (!valid || !complete) {
                                  this.modelService.getInvalidEntities(engineKey).subscribe({
                                    next: (invalidEntities: string[]) => {
                                      this.invalidEntities.set(invalidEntities);
                                      this.modelService.getMessages(engineKey).subscribe({
                                        next: (messages) => {
                                          this.messages.set(messages);
                                          subject.next();
                                          subject.complete();
                                        }, error: (error) => subject.error(error)
                                      });
                                    }, error: (error) => subject.error(error)
                                  });
                                } else {
                                  subject.next();
                                  subject.complete();
                                }
                              }, error: (error) => subject.error(error)
                            });
                          }, error: (error) => subject.error(error)
                        });
                      }, error: (error) => subject.error(error)
                    });
                  }, error: (error) => subject.error(error)
                });
              }, error: (error) => subject.error(error),
            });
          }, error: (error) => subject.error(error)
        });
      }, error: (error) => subject.error(error)
    });
    return subject.asObservable();
  }

  private selectFirstValidOptions(engineKey: string): Observable<string> {
    const subject: Subject<string> = new Subject<string>();
    const observables: Observable<string>[] = [];
    for (const entity of this.selections()) {
      if (StringUtils.isBlank(entity.Value)) {
        const allowable: GuruAllowable = this.getFirstValidAllowable(entity);
        if (allowable) {
          observables.push(this.modelService.setValue(entity.Name, engineKey, allowable.Value));
        }
      }
    }
    if (observables.length > 0) {
      forkJoin(observables).subscribe({
        error: (error) => subject.error(error),
        complete: () => subject.complete()
      });
    } else {
      subject.complete();
    }
    return subject.asObservable();
  }

  private getFirstValidAllowable(entity: GuruEntity): GuruAllowable {
    if ((entity?.Allowables?.length || 0) > 0) {
      for (const allowable of entity.Allowables) {
        if (allowable.IsValid) {
          return allowable;
        }
      }
    }
    return undefined!;
  }

  loadSkusAndPrices(engineKey: string): void {
    this.skus.set([]);
    this.sfrs.set([]);
    if (this.showSkusAndPrices()) {
      this.isBusy.set(true);
      this.modelService.getConfigurationData(engineKey, this.entity().priceBasisDate, this.entity().priceListId).subscribe({
        next: (data) => {
          this.configurationLeadTime.set(this.determineBestLeadTime(data.bestLeadTime));
          this.skus.set(data.skus);
          if ((this.affectedLines()?.length || 0) > 0) {
            this.sfrManagementService.getSfrEntries(this.entity().id.entityId, this.entity().id.entityRevision, this.affectedLines()[0].lineId).subscribe({
              next: (sfrEntries) => {
                this.sfrs.set(sfrEntries);
                this.isBusy.set(false);
              }, error: (error) => this.handleError(error)
            });
          } else {
            this.isBusy.set(false);
          }
        }, error: (error) => this.handleError(error)
      });
    }
  }

  private determineBestLeadTime(asConfigured: IecLeadTime): IecLeadTime {
    return this.sfrsAreApplied() ? this.slowestLeadTime() : asConfigured;
  }

  private sfrsAreApplied(): boolean {
    return this.sfrs() && this.sfrs().length > 0 && this.sfrs().filter(sfr => !sfr.ignore).length > 0;
  }

  saveConfiguration(): void {
    if (this.canSave()) {
      this.isBusy.set(true);
      this.errorMessage.set(undefined!);
      const input: SureFlowEntityRatingInput = new SureFlowEntityRatingInput();
      input.units = [];
      for (const unit of this.selectedUnits()) {
        if (this.ratingUnitData()) {
          unit.unitData.heatingTargetTotalLoad = this.ratingUnitData().heatingTargetTotalLoad;
          unit.unitData.coolingTargetTotalLoad = this.ratingUnitData().coolingTargetTotalLoad;
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb = this.ratingUnitData().sureFlowRatingUnit.request.primaryCoilRequest.enteringDryBulb;
          unit.unitData.sureFlowRatingUnit.request.primaryCoilRequest.enteringWetBulb = this.ratingUnitData().sureFlowRatingUnit.request.primaryCoilRequest.enteringWetBulb;
          if (this.ratingUnitData().sureFlowRatingUnit.request.secondaryCoilRequest) {
            unit.unitData.sureFlowRatingUnit.request.secondaryCoilRequest.enteringDryBulb = this.ratingUnitData().sureFlowRatingUnit.request.secondaryCoilRequest.enteringDryBulb;
          }
          if (this.ratingUnitData().sureFlowRatingUnit.request.electricHeatRequest) {
            unit.unitData.sureFlowRatingUnit.request.electricHeatRequest.enteringDryBulb = this.ratingUnitData().sureFlowRatingUnit.request.electricHeatRequest.enteringDryBulb;
          }
        }
        const unitInput: SureFlowEntityUnitRatingInput = new SureFlowEntityUnitRatingInput();
        if (this.tagInformation) {
          const tagInformation = this.tagInformation.filter(tagInfo => tagInfo.sequence === unit.sequence);
          if (tagInformation.length === 1) {
            unitInput.tagInformation = tagInformation[0];
          }
        }
        unitInput.unit = unit;
        input.units.push(unitInput);
      }
      input.loop = this.selectedLoop();
      input.engineKey = this.engineKey() || '';
      input.updatedCharacteristics = this.characteristics();
      this.unitService.saveEntityLoopUnits(input).subscribe({
        next: () => {
          if (this.addMode()) {
            this.configurationService.setLastRatedUnit(input.units[0].unit);
          }
          this.backToEntityLoops();
        },
        error: (error) => this.handleError(error),
      });
    }
  }

  showMessages(): void {
    this.dialogService.showConfiguratorMessagesDialog(this.messages());
  }

  updateTags(tagInformation: TagInformation[]): void {
    this.tagInformation = JSON.parse(JSON.stringify(tagInformation));
  }

  sfrFlagChanged(sfrEntry: SfrEntry): void {
    if (this.showSkusAndPrices()) {
      const otherUnitQuantity = this.affectedLines()[0].quantityExtended - this.selectedUnits().length;
      if (otherUnitQuantity > 0) {
        const messages: string[] = [];
        messages.push('There are ' + otherUnitQuantity + ' other units on the same line that are affected by this SFR.');
        messages.push('Would you like to update the other units as well?');
        this.dialogService.showConfirmationDialog(messages, 'Yes, update all the units', 'No, update just this unit').subscribe({
          next: (response) => {
            this.isBusy.set(true);
            this.errorMessage.set(undefined!);
            const input: SureFlowEntityRatingInput = new SureFlowEntityRatingInput();
            input.engineKey = this.engineKey();
            input.loop = this.selectedLoop();
            if (response) {
              this.unitService.getEntityLoopUnits(this.selectedLoop().id).subscribe({
                next: (loopUnits) => {
                  input.units = this.buildRatingInputList(loopUnits);
                  this.unitService.toggleSfrFlag(sfrEntry.sfrNumber, input).subscribe({
                    next: () => this.isBusy.set(false),
                    error: (error) => this.handleError(error)
                  });
                }
              });
            } else {
              input.units = this.buildRatingInputList(this.selectedUnits());
              this.unitService.toggleSfrFlag(sfrEntry.sfrNumber, input).subscribe({
                next: () => this.isBusy.set(false),
                error: (error) => this.handleError(error)
              });
            }
          }
        });
      }
    }
  }

  private buildRatingInputList(units: EntitySureFlowUnit[]): SureFlowEntityUnitRatingInput[] {
    const inputs: SureFlowEntityUnitRatingInput[] = [];
    for (const unit of units) {
      const input: SureFlowEntityUnitRatingInput = new SureFlowEntityUnitRatingInput();
      input.unit = unit;
      inputs.push(input);
    }
    return inputs;
  }

  canSave(): boolean {
    const selectedSystemType = this.getSelectedSystemType();
    if (!selectedSystemType) {
      return false;
    }
    if ('C' === selectedSystemType.primaryCoilApplication) {
      if (StringUtils.isEmptyNumericValue(this.ratingUnitData()?.coolingTargetTotalLoad)) {
        return false;
      }
    }
    if ('H' === selectedSystemType.primaryCoilApplication || 'H' === selectedSystemType.secondaryCoilApplication) {
      if (StringUtils.isEmptyNumericValue(this.ratingUnitData()?.heatingTargetTotalLoad)) {
        return false;
      }
    }
    return true;
  }

}
